"use client";

import { Eye } from "@untitledui/icons";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { useToast } from "@/components/application/toast/ToastProvider";
import { Button } from "@/components/base/buttons/button";
import { useImpersonation } from "@/hooks/admin/useImpersonation";
import { useSession } from "@/lib/auth-client";

export function ImpersonationBanner() {
	const router = useRouter();
	const { showToast } = useToast();
	const { data: session } = useSession();
	const { stop, isStopping } = useImpersonation();
	const [exitError, setExitError] = useState<string | null>(null);

	const impersonatedBy = (
		session as { session?: { impersonatedBy?: string | null } } | null | undefined
	)?.session?.impersonatedBy;
	const targetEmail = session?.user?.email;
	const targetUserId = session?.user?.id;

	if (!impersonatedBy || !targetEmail) {
		return null;
	}

	const handleExit = async () => {
		setExitError(null);
		try {
			const restored = await stop();
			const staffRole = restored?.role;
			const usersBase = staffRole === "support" ? "/support/users" : "/admin/users";
			if (targetUserId) {
				router.push(`${usersBase}/${targetUserId}`);
			} else {
				router.push(usersBase);
			}
			router.refresh();
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to exit impersonation. Sign in again.";
			setExitError(message);
			showToast({
				type: "error",
				message,
				duration: 8000,
			});
		}
	};

	return (
		<div className="sticky top-0 z-50 w-full border-b border-violet-300 bg-violet-50 px-4 py-3 text-center dark:border-violet-800 dark:bg-violet-950">
			<div className="page-shell flex flex-wrap items-center justify-center gap-3">
				<div className="flex items-center gap-2">
					<Eye className="size-5 shrink-0 text-violet-600 dark:text-violet-300" />
					<p className="text-sm font-medium text-violet-900 dark:text-violet-100">
						<span className="font-semibold">Viewing as</span> {targetEmail}
						<span className="ml-1 font-normal text-violet-700 dark:text-violet-300">
							— actions apply as this user
						</span>
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Button
						color="secondary"
						size="sm"
						onClick={() => void handleExit()}
						isDisabled={isStopping}
						isLoading={isStopping}
					>
						Exit impersonation
					</Button>
					{exitError ? (
						<a
							href="/login"
							className="text-xs font-medium text-violet-800 underline dark:text-violet-200"
						>
							Sign in again
						</a>
					) : null}
				</div>
			</div>
		</div>
	);
}
